/**
 * HeadersEditor — 自定义 HTTP 请求头编辑器
 * 键值对列表可视化编辑（避免手写易错），支持从文本/JSON 导入解析为列表。
 * value 可含 ${var} 占位符（详见 webhookPlaceholders）。
 */
import { useState } from 'react'
import {
  makeStyles,
  tokens,
  Input,
  Button,
  Badge,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogContent,
  DialogActions,
  Textarea,
  Field,
  InfoLabel,
} from '@fluentui/react-components'
import { AddRegular, DeleteRegular, ArrowImportRegular } from '@fluentui/react-icons'
import { DialogCloseTitle } from '@/components/DialogCloseTitle'
import { WEBHOOK_PLACEHOLDERS } from '@/components/webhookPlaceholders'

export interface HeaderItem {
  name: string
  value: string
}

interface Props {
  /** 当前请求头列表 */
  items: HeaderItem[]
  onChange: (items: HeaderItem[]) => void
}

const useStyles = makeStyles({
  wrap: { display: 'flex', flexDirection: 'column', gap: '8px' },
  row: { display: 'flex', gap: '8px', alignItems: 'center' },
  nameInput: { flex: '0 0 220px', minWidth: '160px' },
  valueInput: { flex: 1, minWidth: '160px' },
  hint: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    alignItems: 'center',
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  chip: {
    cursor: 'pointer',
    ':hover': { opacity: 0.8 },
  },
  toolbar: { display: 'flex', gap: '8px', alignItems: 'center' },
})

/** 解析导入文本 → HeaderItem[]（支持 "Name: Value" 行，或 JSON 对象） */
export function parseHeadersImport(raw: string): HeaderItem[] {
  const trimmed = raw.trim()
  if (!trimmed) return []
  // 尝试 JSON 对象：{ "X-One": "a", "Content-Type": "application/json" }
  try {
    const obj = JSON.parse(trimmed)
    if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
      return Object.entries(obj).map(([name, value]) => ({
        name,
        value: typeof value === 'string' ? value : JSON.stringify(value),
      }))
    }
  } catch {
    /* fall through to line parsing */
  }
  // 逐行 "Name: Value"
  const out: HeaderItem[] = []
  for (const line of trimmed.split(/\r?\n/)) {
    const idx = line.indexOf(':')
    if (idx <= 0) continue
    const name = line.slice(0, idx).trim()
    const value = line.slice(idx + 1).trim()
    if (name) out.push({ name, value })
  }
  return out
}

export default function HeadersEditor({ items, onChange }: Props) {
  const styles = useStyles()
  const [importOpen, setImportOpen] = useState(false)
  const [importText, setImportText] = useState('')

  const addRow = () => onChange([...items, { name: '', value: '' }])
  const updateRow = (i: number, patch: Partial<HeaderItem>) =>
    onChange(items.map((it, j) => (j === i ? { ...it, ...patch } : it)))
  const removeRow = (i: number) => onChange(items.filter((_, j) => j !== i))

  const applyImport = () => {
    const parsed = parseHeadersImport(importText)
    if (parsed.length === 0) {
      // 空结果：保留现有，仅关闭
      setImportOpen(false)
      setImportText('')
      return
    }
    onChange([...items, ...parsed])
    setImportOpen(false)
    setImportText('')
  }

  return (
    <div className={styles.wrap}>
      {items.map((it, i) => (
        <div key={i} className={styles.row}>
          <Input
            className={styles.nameInput}
            placeholder="X-Api-Key"
            value={it.name}
            onChange={(_, d) => updateRow(i, { name: d.value })}
          />
          <Input
            className={styles.valueInput}
            placeholder="值（可含 ${mail_content} 等占位符）"
            value={it.value}
            onChange={(_, d) => updateRow(i, { value: d.value })}
          />
          <Button appearance="subtle" size="small" icon={<DeleteRegular />} aria-label="删除" onClick={() => removeRow(i)} />
        </div>
      ))}

      <div className={styles.toolbar}>
        <Button appearance="subtle" size="small" icon={<AddRegular />} onClick={addRow}>
          添加请求头
        </Button>
        <Button appearance="subtle" size="small" icon={<ArrowImportRegular />} onClick={() => { setImportText(''); setImportOpen(true) }}>
          导入
        </Button>
      </div>

      <div className={styles.hint}>
        <span>可用占位符：</span>
        {WEBHOOK_PLACEHOLDERS.map((p) => (
          <Badge
            key={p.token}
            className={styles.chip}
            size="small"
            appearance="outline"
            color="informative"
            title={p.hint}
            onClick={() => {
              const last = items[items.length - 1]
              if (last) {
                updateRow(items.length - 1, { value: last.value + p.token })
              } else {
                onChange([{ name: '', value: p.token }])
              }
            }}
          >
            {p.token}
          </Badge>
        ))}
      </div>

      <Dialog open={importOpen} onOpenChange={(_, d) => !d.open && setImportOpen(false)} modalType="alert">
        <DialogSurface>
          <DialogBody>
            <DialogCloseTitle title="导入请求头" onClose={() => setImportOpen(false)} />
            <DialogContent>
              <Field
                label={
                  <InfoLabel info="支持逐行 “Name: Value” 格式，或 JSON 对象格式，如 {&quot;X-Api-Key&quot;: &quot;abc&quot;}。导入后追加到当前列表。">
                    粘贴内容
                  </InfoLabel>
                }
              >
                <Textarea
                  rows={6}
                  placeholder={'X-Api-Key: your-key\nContent-Type: application/json'}
                  value={importText}
                  onChange={(_, d) => setImportText(d.value)}
                />
              </Field>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setImportOpen(false)}>取消</Button>
              <Button appearance="primary" onClick={applyImport}>导入</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}